import { motion } from "framer-motion";
import { Briefcase, FileCheck, Home, Handshake, Lightbulb, FileText, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "gruendung",
    icon: Briefcase,
    title: "Unternehmensgründung in Florida",
    copy: "Von der richtigen Unternehmensform (LLC oder Corporation) über die Registrierung bis zur Steuernummer (EIN), dem Geschäftskonto und Ihrer Homepage für den US-Markt: Wir begleiten Ihre Firmengründung in den USA Schritt für Schritt – auf Deutsch und ohne Umwege.",
    link: { href: "/unternehmensgruendung-usa", label: "Mehr zur Gründung" },
  },
  {
    id: "immigration",
    icon: FileCheck,
    title: "Immigration & Visa-Service",
    copy: "E-2, L-1 oder ein anderer Weg: Wir klären mit Ihnen, welches Visum zu Ihrem Vorhaben passt, bereiten alles vor und begleiten Ihre Antragstellung komplett – persönlich und auf Deutsch.",
    link: { href: "/visumsberatung-usa", label: "Mehr zur Visumsberatung" },
  },
  {
    id: "relocation",
    icon: Home,
    title: "Relocation & Ankommen",
    copy: "Umzug, Wohnungssuche, Versicherungen, Führerschein, Schulen: Wir organisieren Ihren Neustart in Florida, damit Sie und Ihre Familie vom ersten Tag an wirklich ankommen.",
    link: { href: "/auswandern-florida", label: "Mehr zur Auswanderung" },
  },
  {
    id: "beratung",
    icon: Handshake,
    title: "Beratung nach der Auswanderung",
    copy: "Unsere Betreuung endet nicht am Flughafen. Wir bleiben an Ihrer Seite – mit laufender Unternehmensberatung, Marketing und Kundengewinnung, einem starken Netzwerk vor Ort und Unterstützung bei allem, was danach kommt.",
    link: { href: "/beratung-nach-auswanderung", label: "Mehr zur Betreuung" },
  },
  {
    id: "business-ideen",
    icon: Lightbulb,
    title: "Fixfertige Business-Ideen",
    copy: "Sie wollen gründen, wissen aber noch nicht womit? Wir haben erprobte, fixfertige Geschäftsideen für den US-Markt – mit echtem Marktbezug statt Luftschlössern und passend zu Ihrer Visumsstrategie, vom E-2 bis zur Green Card.",
    link: { href: "/business-ideen", label: "Ideen ansehen" },
  },
  {
    id: "businessplan",
    icon: FileText,
    title: "Businessplan-Erstellung",
    copy: "Wir erstellen Ihren Businessplan – visumstauglich für E-2-, EB-2-NIW- und L-1A-Anträge und überzeugend für Banken und Konsulate. Kein Textbaustein, sondern Ihr echter Plan für Florida.",
    link: { href: "/businessplan-erstellung-usa", label: "Mehr zum Businessplan" },
  },
];

export default function Services() {
  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="border-t border-line bg-sand-deep py-24 lg:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.3em] text-clay"
              data-testid="services-eyebrow"
            >
              Leistungen
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl"
              data-testid="services-title"
            >
              Unsere Leistungen in{" "}
              <span className="italic text-clay">Florida</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-base leading-relaxed text-ink-muted"
              data-testid="services-intro"
            >
              Alles aus einer Hand: Gründung, Immigration, Relocation – und
              Beratung, die auch nach der Auswanderung nicht aufhört.
            </motion.p>
            <motion.a
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.3 }}
              href="/kontakt"
              data-testid="services-cta"
              className="group mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-clay"
            >
              Unverbindlich anfragen
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </motion.a>
          </div>
        </div>

        <div className="lg:col-span-8">
          <ul className="border-t border-line" data-testid="services-list">
            {SERVICES.map((service, index) => (
              <motion.li
                key={service.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="group grid grid-cols-[auto_1fr] gap-6 border-b border-line py-10 transition-colors hover:bg-sand sm:grid-cols-[auto_auto_1fr] sm:gap-10"
                data-testid={`service-card-${service.id}`}
              >
                <span className="font-serif text-sm italic text-ink-muted">
                  0{index + 1}
                </span>
                <service.icon
                  className="mt-1 hidden h-6 w-6 text-clay sm:block"
                  strokeWidth={1.25}
                />
                <div>
                  <h3 className="font-serif text-2xl tracking-tight text-ink transition-colors group-hover:text-clay sm:text-3xl">
                    {service.title}
                  </h3>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink-muted md:text-base">
                    {service.copy}
                  </p>
                  {service.link && (
                    <a
                      href={service.link.href}
                      data-testid={`service-link-${service.id}`}
                      className="group/link mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-clay"
                    >
                      {service.link.label}
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5" />
                    </a>
                  )}
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
